import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';

import { catchError, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { ZraApiError } from '../zra-product-detail/zra-product-detail.service';

export interface ZraTaxCode {
  /**
   * The ZRA tax code
   */
  zraCode: string;

  /**
   * The Wirepick code
   */
  wirepickCode: string;

  /**
   * The description of the tax code
   */
  description: string;

  /**
   * The explanation of the tax code
   */
  explanation: string;

  /**
   * The tax rate
   */
  taxRate: number;
}

@Injectable({
  providedIn: 'root',
})
export class ZraTaxCodesService {
  private base: string;
  private version: string;
  private taxCodesCache: { [entityId: string]: { data: ZraTaxCode[], timestamp: number } } = {};
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private http: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  getTaxCodes(entityId: string): Observable<ZraTaxCode[]> {
    const cachedData = this.getCachedTaxCodes(entityId);
    if (cachedData) {
      return of(cachedData);
    }

    return this.http
      .get<ZraTaxCode[]>(
        `${this.base}api/v${this.version}/${entityId}/ZraTaxCodes`
      )
      .pipe(
        shareReplay(1),
        map(taxCodes => {
          this.cacheTaxCodes(entityId, taxCodes);
          return taxCodes;
        }),
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'Failed to get tax codes')
        )
      );
  }

  private getCachedTaxCodes(entityId: string): ZraTaxCode[] | null {
    const cachedItem = this.taxCodesCache[entityId];
    if (cachedItem && (Date.now() - cachedItem.timestamp) < this.CACHE_DURATION) {
      return cachedItem.data;
    }
    return null;
  }

  private cacheTaxCodes(entityId: string, taxCodes: ZraTaxCode[]): void {
    this.taxCodesCache[entityId] = {
      data: taxCodes,
      timestamp: Date.now()
    };
  }

  private handleError(
    error: HttpErrorResponse,
    defaultMessage: string
  ): Observable<never> {
    let errorMessage = defaultMessage;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Server-side error
      try {
        const apiError = error.error as ZraApiError;
        errorMessage =
          apiError.message ||
          `Server error: ${error.status} - ${error.statusText}`;
        if (apiError.details) {
          errorMessage += ` (${apiError.details})`;
        }
      } catch {
        errorMessage = `Server error: ${error.status} - ${
          error.statusText || defaultMessage
        }`;
      }
    }

    return throwError(() => ({
      message: errorMessage,
      status: error.status,
      originalError: error,
    }));
  }
}
